import React from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useLanguage } from "../LanguageProvider";
import { AlertCircle, ChevronRight, Trophy, Award, Medal, Check } from "lucide-react";
import { Header } from "../components/layout/Header";
import { groupOverdueDebts } from "../utils/calculator";
import { PageLoader } from "../components/common/LoadingStates";
import "../styles/pages/bad-debts.css";

export const BadDebts = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const { allUnpaidUsers, loading } = useOutletContext();

  // Centralized grouping logic from calculator
  const grouped = groupOverdueDebts(allUnpaidUsers);
  const sortedGrouped = Object.values(grouped).sort((a, b) => b.totalAmount - a.totalAmount);

  // Calculate rankings based on unique amounts
  const uniqueAmounts = Array.from(new Set(sortedGrouped.map((g) => g.totalAmount.toFixed(2))));
  const amountToRank = {};
  uniqueAmounts.forEach((amount, idx) => {
    amountToRank[amount] = idx + 1;
  });

  return (
    <PageLoader loading={loading}>
      <Header title={t("bad_debt")} subtitle={t("bad_debt_ranking")} />

      <div className="container">
        <div className="bad-debts-wrapper">
          <div className="debt-stats-row">
            <div className="debt-stat-card">
              <span className="debt-stat-value">{sortedGrouped.length}</span>
              <span className="debt-stat-label">{t("players")}</span>
            </div>
            <div className="debt-stat-card highlight">
              <span className="debt-stat-value">
                {sortedGrouped.reduce((sum, g) => sum + g.totalAmount, 0).toFixed(0)}
              </span>
              <span className="debt-stat-label">{t("total")} kr</span>
            </div>
          </div>

          {sortedGrouped.length > 0 ? (
            <div className="debt-leaderboard">
              {sortedGrouped.map((group) => {
                const rank = amountToRank[group.totalAmount.toFixed(2)];
                let medalIcon = null;
                if (rank === 1) medalIcon = <Trophy size={26} color="#b45309" fill="#fde68a" />;
                else if (rank === 2) medalIcon = <Medal size={26} color="#475569" fill="#e2e8f0" />;
                else if (rank === 3) medalIcon = <Award size={26} color="#9a3412" fill="#fed7aa" />;

                return (
                  <div key={group.participantId} className={`debt-card rank-${rank}`}>
                    <div className="debt-card-header">
                      <div className="debt-rank">
                        {medalIcon ? <span className="rank-medal">{medalIcon}</span> : <span className="rank-number">#{rank}</span>}
                      </div>

                      <div className="debt-user-info">
                        <div className={`debt-avatar ${group.linked_user_id ? 'avatar-ring-verified' : ''}`}>
                          {group.profile_image_url ? (
                            <img src={group.profile_image_url} alt={group.name} />
                          ) : (
                            <div className="avatar-placeholder">{(group.name === "unknown" ? t("unknown") : group.name).charAt(0).toUpperCase()}</div>
                          )}
                          {group.linked_user_id && (
                            <span className="verified-dot"><Check size={8} /></span>
                          )}
                        </div>
                        <div className="debt-user-details">
                          <span className="debt-user-name">
                            {group.name === "unknown" ? t("unknown") : group.name}
                          </span>
                          <span className="debt-session-count">
                            {group.sessions.length} {t("sessions_list").toLowerCase()}
                          </span>
                        </div>
                      </div>

                      <div className="debt-total">
                        <span className="debt-total-label">{t("total")} {t("unpaid").toLowerCase()}</span>
                        <span className="debt-amount-wrapper">
                          <span className="debt-amount">{group.totalAmount.toFixed(2)}</span>
                          <span className="debt-currency">kr</span>
                        </span>
                      </div>
                    </div>

                    <div className="debt-card-body">
                      <div className="debt-sessions-list">
                        {group.sessions.map((session, idx) => (
                          <div
                            key={session.id || idx}
                            className="debt-session-item"
                            onClick={() => navigate(`/sessions/${session.id}`)}
                          >
                            <div className="debt-session-left">
                              <div className="debt-session-date-box">
                                <span className="debt-session-month">
                                  {new Date(session.sessionDate).toLocaleDateString(language === "vi" ? "vi-VN" : "en-US", { month: "short" })}
                                </span>
                                <span className="debt-session-day">
                                  {new Date(session.sessionDate).toLocaleDateString(language === "vi" ? "vi-VN" : "en-US", { day: "2-digit" })}
                                </span>
                              </div>
                              <span className="debt-session-name">
                                {session.sessionName || t("session_default_name")}
                              </span>
                            </div>
                            <div className="debt-session-right">
                              <span className="debt-session-amount">{session.amount.toFixed(2)}</span>
                              <ChevronRight size={18} className="debt-icon-next" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bad-debts-empty">
              <AlertCircle size={64} />
              <h3>{t("all_paid")}</h3>
              <p>{t("no_bad_debts")}</p>
            </div>
          )}
        </div>
      </div>
    </PageLoader>
  );
};
